import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ITimelineEntry } from '@commons/history/timeline/models/timeline-entry';
import { EXTERNAL_ROUTING_NAME } from '@history/core/history-deprecated.service';
import moment from 'moment';
import { filter, from, groupBy, map, mergeMap, Observable, OperatorFunction, pipe, toArray } from 'rxjs';

import { MomentService, TranslateService, UserUtil, ChannelType } from '@contact/upgrade';

interface Timeline {
  key: number;
  label: string;
  entries: ITimelineEntry[];
}

@Component({
  selector: 'cng-timeline-history',
  templateUrl: 'timeline-history.component.html',
  styleUrls: ['./timeline-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineHistoryComponent implements OnChanges {
  @Input() activeCompany: boolean;
  @Input() timelines: ITimelineEntry[];
  @Input() selectInteractionId: string;
  @Output() elementSelectedFn: EventEmitter<ITimelineEntry> = new EventEmitter();

  currentYear: number;

  // Return timeline by month for current year
  head$: Observable<Timeline[]>;
  // Return timeline by year for past years
  tail$: Observable<Timeline[]>;

  constructor(private readonly momentService: MomentService, private readonly translateService: TranslateService) {
    this.currentYear = new Date().getFullYear();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { ['timelines']: timelines } = changes;
    if (!(timelines && timelines.currentValue && timelines.previousValue !== timelines.currentValue)) {
      return;
    }

    // Retrieve data send to component by his parent
    let newValue = timelines.currentValue as ITimelineEntry[];

    // Sort retrieved data by most recent date to oldest
    newValue = newValue.sort((e1, e2) => new Date(e2.beginDate).getTime() - new Date(e1.beginDate).getTime());

    const source$ = from(newValue);
    const year$ = source$.pipe(
      this.groupOp(
        entry => moment(entry.beginDate).year(),
        key => `${key}`
      )
    );
    const currentYear$ = year$.pipe(filter(({ key }) => key === this.currentYear));
    const pastYear$ = year$.pipe(filter(({ key }) => key < this.currentYear));
    const monthForCurrentYear$ = currentYear$.pipe(
      mergeMap(({ entries }) =>
        from(entries).pipe(
          this.groupOp(
            entry => moment(entry.beginDate).month(),
            key => this.momentService.getMoment().month(key).format('MMMM')
          )
        )
      )
    );

    // Sending wrappers to template
    this.head$ = monthForCurrentYear$.pipe(toArray());
    this.tail$ = pastYear$.pipe(toArray());
  }
  // Send event to parent (history modal) for refresh modal content
  select(row: ITimelineEntry): void {
    this.elementSelectedFn.emit(row);
  }

  // Function to group by timelineEntry by a defined selector (by month or by year for this component)
  groupOp(projector: (entry: ITimelineEntry) => number, toLabel: (key: number) => string): OperatorFunction<ITimelineEntry, Timeline> {
    return pipe(
      groupBy(
        entry => projector(entry),
        entry => entry
      ),
      mergeMap(group =>
        group.pipe(
          toArray(),
          map(entries => ({
            key: group.key,
            label: toLabel(group.key),
            entries,
          }))
        )
      )
    );
  }

  computeAgentName(contactEntry) {
    if (contactEntry.csrFirstname && contactEntry.csrFirstname.indexOf(EXTERNAL_ROUTING_NAME) !== -1) {
      return this.translateService.instant('history:viewInteractions.' + EXTERNAL_ROUTING_NAME);
    }

    return UserUtil.fullname({
      firstname: contactEntry.csrFirstname,
      lastname: contactEntry.csrLastname,
    });
  }

  isPhone(selected: ChannelType): boolean {
    return selected === ChannelType.PHONE || selected == ChannelType.UNITARY_PHONE;
  }
}
